import { app } from '../../app';
import { CollideEvent, CollideId } from '../../defs/collide';
import type { BlockEntity } from '../../entities/BlockEntity';
import { despawnBlockEntity } from '../../entities/BlockEntity';
import { GameScene } from '../../GameScene';
import { mapGetPan } from '../../util/mapTools';
import { PowerComboEffect } from '../effects/PowerComboEffect';
import { RocketEffect } from '../effects/RocketEffect';
import { CollideHandler, ICollision } from './ICollision';

/*
    rocket block collision resolver
*/
export class RocketCollision implements ICollision {
  // fields
  //-------------------------------------------------------------------------
  // input
  private readonly _scene: GameScene;
  private readonly _entity: BlockEntity;
  // map
  private readonly _resolvers: { [key in CollideId]?: CollideHandler } = {
    tap: this._resolveTap,
    attack: this._resolveAttack,
  };

  // init
  //-------------------------------------------------------------------------
  constructor(scene: GameScene, entity: BlockEntity) {
    this._scene = scene;
    this._entity = entity;
  }

  // impl
  //-------------------------------------------------------------------------
  public async resolve(event: CollideEvent): Promise<boolean> {
    return this._resolvers[event.id]?.call(this, event) ?? false;
  }

  // private: resolvers
  //-------------------------------------------------------------------------
  private async _resolveTap(event: CollideEvent) {
    // attempt potential combo else launch rocket
    const combo = new PowerComboEffect(this._scene, this._entity);
    if (!(await combo.execute())) await this._launch();

    return true;
  }

  private async _resolveAttack(event: CollideEvent) {
    // launch rocket
    await this._launch();
    return true;
  }

  // private: actions
  //-------------------------------------------------------------------------
  private async _launch() {
    const entity = this._entity;

    // play sound
    app.sound.play('puzzle-rocket-activate', {
      dupes: 3,
      pan: mapGetPan(entity.c.position.mapPosition),
    });

    // determine axis
    const axis =
      entity.c.block.blockId === 'rocketHorizontal' ? 'horizontal' : 'vertical';

    // despawn block
    despawnBlockEntity(this._scene, entity);

    // notify
    this._scene.events.publish({ id: 'power', type: 'rocket' });

    // execute rocket effect
    return new RocketEffect(this._scene, {
      position: entity.c.position.mapPosition,
      axis,
    }).execute();
  }
}
